import React, {useEffect, useState} from 'react';
import {Modal} from 'react-bootstrap';
import {useNavigate} from 'react-router-dom';
import {useRestockedTrackedProducts} from '../../../product/application/useRestockedTrackedProducts';
import {AddRestockedToCartButton} from '../../../product/ui/AddRestockedToCartButton';
import {PopupStorageService} from '../../../notification/services/PopupStorageService';
import {PriceWithFallback} from '../../../common/ui/PriceWithFallback';
import {useComercialContext} from '../../../comercial/ComercialContext';
import {isSuccess} from '../../../common/domain/RemoteData.model';

/**
 * Popup que se muestra una vez por sesión al entrar en el portal cuando
 * algún producto pendiente vuelve a tener stock suficiente para la
 * cantidad que el cliente quería.
 */
export const RestockedTrackedPopup = () => {
    const {trackedData, restocked, refresh, removeLocally} = useRestockedTrackedProducts();
    const {actingAsCliente} = useComercialContext();
    const [show, setShow] = useState(false);
    const navigate = useNavigate();

    const popupKey = `restocked_${actingAsCliente?.numCliente ?? 'self'}`;

    useEffect(() => {
        if (!isSuccess(trackedData) || PopupStorageService.hasBeenSeen(popupKey)) {
            return;
        }
        if (restocked.length > 0) {
            PopupStorageService.markAsSeen(popupKey);
            setShow(true);
        }
        // Solo debe evaluarse cuando termina la carga de pendientes
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [trackedData, popupKey]);

    const handleClose = () => setShow(false);

    const handleAdded = (code) => {
        removeLocally(code);
        if (restocked.length <= 1) {
            setShow(false);
        }
    };

    const handleGoToProduct = (code) => {
        setShow(false);
        navigate(`/articulos/${encodeURIComponent(code)}`);
    };

    if (!show || restocked.length === 0) {
        return null;
    }

    return (
        <Modal show={show} onHide={handleClose} centered size="lg">
            <Modal.Header closeButton>
                <Modal.Title className="h5">¡Ya hay stock de tus productos pendientes!</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p className="text-muted">
                    Estos productos que tenías pendientes vuelven a estar disponibles
                    en la cantidad que querías:
                </p>
                <ul className="list-group">
                    {restocked.map((product) => (
                        <li key={product.code} className="list-group-item">
                            <div className="d-flex flex-column flex-md-row align-items-md-center gap-3">
                                <img src={product.imageUrl}
                                    alt={product.name}
                                    style={{width: '64px', height: '64px', objectFit: 'contain', cursor: 'pointer'}}
                                    onClick={() => handleGoToProduct(product.code)}
                                />
                                <div className="flex-fill">
                                    <span role="button"
                                        className="fw-semibold"
                                        onClick={() => handleGoToProduct(product.code)}>
                                        {product.name}
                                    </span>
                                    <br/>
                                    <small className="text-muted">
                                        REF: {product.code} · Querías {product.trackedQuantity ?? 1} ud.
                                    </small>
                                    <div>
                                        <PriceWithFallback data={{
                                            sellingPrice: product.price?.bestPrice,
                                            basePrice: product.price?.value
                                        }}/>
                                    </div>
                                </div>
                                <div style={{minWidth: '200px'}}>
                                    <AddRestockedToCartButton
                                        product={product}
                                        onAdded={handleAdded}
                                        onFailed={refresh}
                                    />
                                </div>
                            </div>
                        </li>
                    ))}
                </ul>
            </Modal.Body>
            <Modal.Footer>
                <button type="button" className="btn btn-secondary" onClick={handleClose}>
                    Cerrar
                </button>
            </Modal.Footer>
        </Modal>
    );
};
